/* Smartap FAQ (sections/st-faq.liquid).
   - Topic chips: toggle buttons (aria-pressed), one pressed at a time; the list is filtered and a
     status line says how many questions show. The chips are revealed here, so without script the
     full list shows and nothing claims to filter.
   - Deep links: /pages/faq#faq-notap opens that answer, marks it and moves focus to its question,
     on load and on a later hash change. A filter hiding it is reset first.
   - Copy: the help panel's button copies the email address and says so.
   - Show first (data-initial): while the All chip is on, questions past that number are folded
     behind the "Show all" button, which unfolds them and moves focus to the first one it showed. A
     topic chip, or a deep link to a folded answer, unfolds them too.
   - Pictures (snippets/st-faq-viz.liquid): an answer's picture settles in each time its question
     opens, and once for a question that starts open, when it comes into view. Not under reduced
     motion. */
if (!customElements.get('st-faq')) {
  customElements.define(
    'st-faq',
    class extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('.st-q')];
        this.chips = this.querySelector('[data-st-chips]');
        this.status = this.querySelector('[data-st-shown]');
        this.initial = Number(this.dataset.initial) || 0;
        this.more = this.querySelector('[data-st-more]');
        this.folded = Boolean(this.initial && this.more);
        if (this.folded) {
          this.more.hidden = false;
          this.more.addEventListener('click', () => {
            const next = this.items.find((q) => q.classList.contains('is-folded'));
            this.#unfold();
            next?.querySelector('summary')?.focus();
          });
          this.#fold();
        }
        if (this.chips) {
          this.chips.hidden = false;
          this.chips.addEventListener('click', (event) => {
            const chip = event.target.closest('[data-topic]');
            if (chip) this.#filter(chip.dataset.topic, true);
          });
        }
        this.#fromHash();
        window.addEventListener('hashchange', () => this.#fromHash());
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const play = (q) => {
            if (!q.querySelector('.st-vz')) return;
            q.classList.remove('is-vplay');
            void q.offsetWidth;
            q.classList.add('is-vplay');
          };
          this.items.forEach((q) =>
            q.addEventListener('toggle', () => (q.open ? play(q) : q.classList.remove('is-vplay')))
          );
          const first = this.items.find((q) => q.open && q.querySelector('.st-vz'));
          if (first && 'IntersectionObserver' in window) {
            const io = new IntersectionObserver(
              (en) => {
                if (!en[0].isIntersecting) return;
                io.disconnect();
                play(first);
              },
              { threshold: 0.4 }
            );
            io.observe(first);
          }
        }
      }

      #fold() {
        this.items.forEach((q, i) => {
          const off = this.folded && i >= this.initial;
          q.classList.toggle('is-folded', off);
          if (off) q.hidden = true;
        });
      }

      #unfold() {
        this.folded = false;
        this.items.forEach((q) => {
          if (q.classList.contains('is-folded')) {
            q.classList.remove('is-folded');
            q.hidden = false;
          }
        });
        if (this.more) this.more.hidden = true;
      }

      #filter(topic, announce) {
        if (topic !== 'all' && this.folded) this.#unfold();
        this.chips?.querySelectorAll('[data-topic]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.topic === topic));
        });
        let shown = 0;
        this.items.forEach((item) => {
          const on = (topic === 'all' || item.dataset.topic === topic) && !item.classList.contains('is-folded');
          item.hidden = !on;
          if (on) shown++;
        });
        if (announce && this.status) this.status.textContent = (this.status.dataset.template || '').replace('__N__', String(shown));
      }

      #fromHash() {
        const id = decodeURIComponent(location.hash.slice(1));
        if (!id) return;
        const item = this.items.find((el) => el.id === id);
        if (!item) return;
        if (item.classList.contains('is-folded')) this.#unfold();
        if (item.hidden) this.#filter('all', true);
        this.items.forEach((el) => el.classList.toggle('is-target', el === item));
        item.open = true;
        setTimeout(() => {
          item.scrollIntoView({ block: 'start' });
          item.querySelector('summary')?.focus({ preventScroll: true });
        }, 0);
      }
    }
  );
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-st-copy]');
  if (!button) return;
  try {
    await navigator.clipboard.writeText(button.dataset.stCopy);
    const label = button.querySelector('span');
    const status = button.parentElement?.querySelector('[data-st-copied]');
    if (status) status.textContent = button.dataset.done || '';
    if (label && button.dataset.done) {
      const before = label.textContent;
      label.textContent = button.dataset.done;
      setTimeout(() => {
        label.textContent = before;
        if (status) status.textContent = '';
      }, 2500);
    }
  } catch (e) {
    /* Clipboard refused: the address is a mailto link beside the button, so nothing is lost. */
  }
});
document.querySelectorAll('[data-st-copy]').forEach((b) => {
  if (navigator.clipboard) b.hidden = false;
});
